/**
 * Admin Layout
 * 
 * @file client/src/components/layout/AdminLayout.jsx
 * @description Admin panel layout with sidebar navigation
 * @project Labs.ChatWithDr
 */

import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { 
  LayoutDashboard, 
  ClipboardList, 
  TestTube2, 
  MessageSquare, 
  Settings, 
  LogOut,
  Menu,
  X,
  Bell,
  Tag,
  Users,
  Mail,
  FileText,
  FlaskConical,
  BookOpen,
  BarChart3,
  MousePointerClick
} from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import Loading from '../common/Loading'

// Admin base path for security
const ADMIN_PATH = '/labsdrnileshpanchal'

// Map each link to required permission
const sidebarLinks = [
  { name: 'Dashboard', href: ADMIN_PATH, icon: LayoutDashboard, exact: true, permission: 'dashboard' },
  { name: 'Orders', href: `${ADMIN_PATH}/orders`, icon: ClipboardList, permission: 'orders' },
  { name: 'Lab Requisition', href: `${ADMIN_PATH}/lab-requisition`, icon: FileText, permission: 'orders' },
  { name: 'Lab Results', href: `${ADMIN_PATH}/lab-results`, icon: FlaskConical, permission: 'orders' },
  { name: 'Lab Tests', href: `${ADMIN_PATH}/tests`, icon: TestTube2, permission: 'tests' },
  { name: 'Categories', href: `${ADMIN_PATH}/categories`, icon: Tag, permission: 'categories' },
  { name: 'Users', href: `${ADMIN_PATH}/users`, icon: Users, permission: 'users' },
  { name: 'Contacts', href: `${ADMIN_PATH}/contacts`, icon: MessageSquare, permission: 'contacts' },
  { name: 'Blog Manager', href: `${ADMIN_PATH}/blog`, icon: BookOpen, permission: 'blog' },
  { name: 'Web Source', href: `${ADMIN_PATH}/web-source`, icon: BarChart3, permission: 'dashboard' },
  { name: 'Booking Source', href: `${ADMIN_PATH}/booking-source`, icon: MousePointerClick, permission: 'dashboard' },
  { name: 'Subscribers', href: `${ADMIN_PATH}/subscribers`, icon: Mail, permission: 'contacts' },
  { name: 'Settings', href: `${ADMIN_PATH}/settings`, icon: Settings, permission: 'settings' },
]

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, loading, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate(`${ADMIN_PATH}/login`)
    }
  }, [loading, isAuthenticated, navigate])

  if (loading) {
    return <Loading message="Loading admin panel..." />
  }

  if (!isAuthenticated) {
    return null
  }

  const handleLogout = () => {
    logout()
    navigate(`${ADMIN_PATH}/login`)
  }

  // Check if user has permission (superadmin has all permissions)
  const hasPermission = (permission) => {
    if (user?.role === 'superadmin') return true
    return user?.permissions?.includes(permission)
  }

  // Filter sidebar links based on user permissions
  const allowedLinks = sidebarLinks.filter(link => hasPermission(link.permission))

  // Role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superadmin': return 'bg-red-500'
      case 'admin': return 'bg-purple-500'
      case 'manager': return 'bg-blue-500'
      case 'viewer': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin': return 'Super Admin'
      case 'admin': return 'Admin'
      case 'manager': return 'Manager'
      case 'viewer': return 'Viewer'
      default: return role
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
            <Link to={ADMIN_PATH} className="flex items-center gap-3">
              <img 
                src="/assets/logo-square.png" 
                alt="ChatWithDr" 
                className="h-8 w-8"
              />
              <span className="text-white font-bold text-lg">Admin</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {allowedLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.href}
                end={link.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`
                }
              >
                <link.icon className="w-5 h-5" />
                {link.name}
              </NavLink>
            ))}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center gap-3 mb-4 px-4 py-2">
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-gray-500 text-xs truncate">{user?.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs text-white ${getRoleBadgeColor(user?.role)}`}>
                  {getRoleLabel(user?.role)}
                </span>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>

            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                target="_blank"
                className="text-sm text-gray-500 hover:text-primary-500 transition-colors"
              >
                View Site →
              </Link>
              <button className="relative p-2 rounded-lg hover:bg-gray-100">
                <Bell className="w-5 h-5 text-gray-500" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}